<template>
  <div v-if="showModal" class="modal-container">
    <div class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40" @click="closeModal"></div>

    <div class="modal-content w-[800px] max-w-[95vw] max-h-[90vh] relative bg-white dark:bg-card m-auto flex-col flex rounded-[24px] z-50 shadow-2xl overflow-hidden">
      <!-- Close button (absolute positioned to match clean design) -->
      <button @click="closeModal" class="absolute top-6 right-6 text-muted-foreground hover:text-foreground transition-colors p-2 rounded-full hover:bg-muted/50 z-10">
        <XIcon class="w-5 h-5" />
      </button>

      <div class="overflow-y-auto px-10 py-12 flex-1 custom-scrollbar">
        <h3 class="text-[24px] font-bold text-foreground mb-10">
          Task Submission
        </h3>

        <div class="space-y-10">
          <div
            class="group border-[1.5px] border-dashed border-border rounded-3xl p-16 flex flex-col items-center justify-center gap-4 hover:border-foreground/30 hover:bg-muted/10 transition-all cursor-pointer relative overflow-hidden"
            @click="$refs.fileInput.click()"
          >
            <input
              type="file"
              ref="fileInput"
              class="hidden"
              @change="handleFileChange"
            />
            <div
              class="w-12 h-12 rounded-full bg-muted/40 flex items-center justify-center group-hover:scale-110 transition-transform"
            >
              <CloudUploadIcon class="w-6 h-6 text-foreground/50" />
            </div>
            <div class="text-center space-y-1">
              <p class="text-[16px] font-bold text-foreground">
                {{
                  submissionFile
                    ? submissionFile.name
                    : "Click to upload or drag and drop"
                }}
              </p>
              <p class="text-[14px] text-muted-foreground/60">
                Max file size: 5MB. Supported formats: .PDF, .DOCX, .TXT, .MD, .PY, .JS, .CSV, etc.
              </p>
            </div>
          </div>

          <div class="space-y-6 max-w-2xl">
            <div class="space-y-4">
              <p class="text-[15px] font-bold text-muted-foreground/80">
                Or if link, submit here:
              </p>
              <div class="space-y-2">
                <label class="text-[14px] font-extrabold text-foreground">URL</label>
                <input
                  v-model="submissionUrl"
                  type="text"
                  placeholder="https://github.com/your-project"
                  class="w-full bg-transparent border border-border rounded-2xl px-6 py-4 text-[16px] focus:outline-none focus:ring-1 focus:ring-foreground/20 transition-all placeholder:text-muted-foreground/30"
                />
              </div>
            </div>
            <button
              @click="submitTask"
              class="px-12 py-4 mt-4 bg-[#2D3139] text-white rounded-full font-bold text-[16px] hover:bg-[#1a1d22] transition-all shadow-md active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
              :disabled="submittingTask || (!submissionUrl && !submissionFile)"
            >
              <LoaderIcon v-if="submittingTask" class="w-5 h-5 animate-spin" />
              {{ submittingTask ? "Submitting..." : "Submit task" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios'
import {
  X as XIcon,
  CloudUpload as CloudUploadIcon,
  Loader as LoaderIcon
} from "lucide-vue-next";

export default {
  name: 'SubmitSubProjectModal',
  components: {
    XIcon,
    CloudUploadIcon,
    LoaderIcon
  },
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    project: {
      type: Object,
      default: null
    },
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      submissionUrl: '',
      submissionFile: null,
      submittingTask: false
    }
  },
  watch: {
    showModal(newVal) {
      if (!newVal) {
        this.submissionUrl = '';
        this.submissionFile = null;
        this.submittingTask = false;
      }
    }
  },
  methods: {
    closeModal() {
      if (!this.submittingTask) {
        this.$emit('close');
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.submissionFile = file;
      }
    },
    async submitTask() {
      if (!this.submissionUrl && !this.submissionFile) {
        this.$toast?.error("Error", "Please provide a link or upload a file.");
        return;
      }

      if (!this.project || !this.projectId) {
        this.$toast?.error("Error", "Missing project data.");
        return;
      }

      try {
        this.submittingTask = true;
        let fileUrl = null;
        let extractedText = null;

        if (this.submissionFile) {
          const uploadData = new FormData();
          uploadData.append("file", this.submissionFile);
          const uploadRes = await apiClient.post(
            "/web/learning-goals/upload-resource",
            uploadData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          if (uploadRes.data?.success) {
            fileUrl = uploadRes.data.url;
            extractedText = uploadRes.data.full_text;
          } else {
            this.$toast?.error(
              "Error",
              uploadRes.data?.message || "Failed to upload file."
            );
            return;
          }
        }

        const taskId = this.project.task_id || this.project.id;
        const response = await apiClient.post(
          `/web/learning-goals/${this.projectId}/tasks/${taskId}/submit-project`,
          {
            submission_url: this.submissionUrl || null,
            submission_file_url: fileUrl || null,
            extracted_text: extractedText || null,
            notes: "",
          }
        );

        this.$toast?.success(
          "Success",
          "Project submitted! Check out the feedback."
        );

        this.$emit('submitted', {
          project: this.project,
          submission: response.data.project_submission
        });
        
        this.submittingTask = false;
        this.closeModal();
      } catch (error) {
        console.error("Error submitting task:", error);
        const errorMsg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to submit task. Please try again.";
        this.$toast?.error("Error", errorMsg);
      } finally {
        this.submittingTask = false;
      }
    }
  }
}
</script>

<style scoped>
.modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: 50;
}
.modal-content {
  position: relative;
  z-index: 60;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.3);
  border-radius: 10px;
}
.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
}
</style>
